import { TenantHandlerParamsType } from '../../../services/tenantHandler/strategy/external-types';
import { AccessToken } from '../../../services/JWeb/types';
import getAuthenticationProviderByToken from '../../../utils/tokenUtils/getAuthenticationProviderByToken';
import { strategyMapping } from './strategyMapping';
import AuthenticationProviderEnum from '../../../config/authenticationProviderEnum';
import { TokenType } from '../../../services/JWeb/JWebEnums';
import { AuthPluginFacade } from '../../../services/JWeb/facades/AuthPluginFacade';
import { internalLogger } from '../../../interface/v1/clientOsLogger';
import InitializationTokenCache from '../../../services/JWeb/cache/InitializationTokenCache';
import { JWebErrorHandler } from '../../../services/JWeb';
import * as JWebTypes from '../../../services/JWeb/types';

/**
 * Enhancer for processing web manifests into app-specific configurations
 * Handles authentication providers and tenant handler strategies based on current user context
 */
export class ManifestWebAppEnhancer {
  /**
   * Processes a web manifest and adapts it for app-specific configurations
   * Updates authentication providers and tenant handler strategies based on current user context
   *
   * @param manifest - The manifest configuration to process
   */
  static async processWebManifestIntoAppManifest(
    manifest: Record<string, any>
  ): Promise<void> {
    let isLoggedIn = false;
    try {
      const { value: actualIsLoggedIn } =
        await AuthPluginFacade.isLoggedIn().then((v) =>
          JWebErrorHandler<{ value: boolean }>(v)
        );
      isLoggedIn = actualIsLoggedIn;
    } catch (error) {
      internalLogger?.error?.e1?.({
        className: 'ManifestWebAppEnhancer',
        methodName: 'processWebManifestIntoAppManifest()',
        message: `Error checking if user is logged in ${error?.message}`,
        fallbackAction: 'Assuming user is not logged in'
      });
    }

    if (!isLoggedIn) {
      /* istanbul ignore next */
      internalLogger?.debug?.d5?.({
        className: 'ManifestWebAppEnhancer',
        methodName: 'processWebManifestIntoAppManifest()',
        message: 'User not logged in, skipping manifest processing'
      });
      return;
    }

    try {
      const appToken = await InitializationTokenCache.getToken({
        tokenProviderOptions: {
          tokenType: TokenType.user
        }
      });

      if (!(appToken as AccessToken)?.tokenValue) {
        console.warn(
          'No valid token available, skipping manifest WebApp processing'
        );
        return;
      }

      const appAuthenticationProvider = getAuthenticationProviderByToken(
        (appToken as AccessToken).tokenValue
      );

      // Set initial authentication provider if not present
      this.setInitialAuthenticationProvider(
        manifest,
        appAuthenticationProvider
      );

      // Update current authentication provider
      this.updateAuthenticationProvider(manifest, appAuthenticationProvider);

      // Update tenant handler strategies
      this.updateTenantHandlerStrategies(manifest, appAuthenticationProvider);
    } catch (error) {
      console.error('Error processing web manifest into app manifest:', error);
      // Don't throw to prevent breaking the initialization process
    }
  }

  /**
   * Sets the initial authentication provider if not already configured
   * @param manifest - The manifest configuration
   */
  private static setInitialAuthenticationProvider(
    manifest: Record<string, any>,
    appAuthenticationProvider: string
  ): void {
    // TODO: Need to update the initialAuthenticationProvider and the authenticationProvider
    // variable names to be more specific and update the application to follow and look for the new names.

    if (!manifest?.services?.login?.initialAuthenticationProvider) {
      manifest.services.login = {
        initialAuthenticationProvider:
          manifest?.services?.login?.authenticationProvider ||
          AuthenticationProviderEnum.authz,
        ...manifest?.services.login
      };
    }
  }

  /**
   * Updates the current authentication provider in the manifest
   * @param manifest - The manifest configuration
   * @param appAuthenticationProvider - Current authentication provider
   */
  private static updateAuthenticationProvider(
    manifest: Record<string, any>,
    appAuthenticationProvider: string
  ): void {
    if (!manifest.services) {
      manifest.services = {};
    }
    if (!manifest.services.login) {
      manifest.services.login = {};
    }

    manifest.services.login.authenticationProvider = appAuthenticationProvider;
  }

  /**
   * Updates tenant handler strategies based on the current authentication provider
   * @param manifest - The manifest configuration
   * @param appAuthenticationProvider - Current authentication provider
   */
  private static updateTenantHandlerStrategies(
    manifest: Record<string, any>,
    appAuthenticationProvider: string
  ): void {
    manifest?.services?.tenantHandler?.tenantHandlerList?.forEach(
      (config: TenantHandlerParamsType) => {
        const newStrategy =
          strategyMapping[appAuthenticationProvider]?.[config.strategy];

        if (newStrategy) {
          config.strategy = newStrategy || config.strategy;
        }
      }
    );
  }
}
